<template>
  <div
    :class="[
      'button-base-block',
      {
        'button-base-block--full-width': fullWidth,
      },
    ]"
  >
    <button 
        :class="[
            'button-base', 
            {'primary-gradient': styles === 'primary-gradient'},
            {'secondary-100': styles === 'secondary-100'},
            {'secondary-200': styles === 'secondary-200'},
            {'full-width': fullWidth},
            {'disabled': isDisabled},
            {'button-base--compact': size === 'compact'}
            ]" 
        :disabled="isDisabled"
        @click="handleClick">
        <div class="button-base__left-row">
            <component v-if="icon" class="button-icon" :is="iconComponents[icon]"/>
            {{ text }} 
        </div>
        <div 
            v-if="subtitile"
            :class="[
                'button-base__subtitle',
                {'regular': subtitleStyle === 'regular'},
                {'medium': subtitleStyle === 'medium'}
            ]"
        >
            {{ subtitile }}
        </div>
    </button>
  </div>
</template>

<script lang="ts" setup>
import Wallet from '@/assets/icons/wallet.svg'
import WalletBlack from '@/assets/icons/wallet-black.svg'
import WalletOff from '@/assets/icons/wallet-off.svg'
import Gear from '@/assets/icons/gear.svg'
import Message from '@/assets/icons/message.svg'
import Referal from '@/assets/icons/referal.svg'
import User from '@/assets/icons/user.svg'
import Quit from '@/assets/icons/quit.svg'
import ChangePassword from '@/assets/icons/change-password.svg'
import { useVibration } from '@/shared/utils/system/vibration'

type buttonStyles = 'secondary-100' | 'secondary-200' | 'primary-gradient'
type buttonIcon =
    | 'Wallet'
    | 'WalletBlack'
    | 'Gear'
    | 'Message'
    | 'Referal'
    | 'User'
    | 'WalletOff'
    | 'Quit'
    | 'ChangePassword'
type subtitleStyles = 'regular' | 'medium'
type vibrationType = 'button' | 'success' | 'error' | 'critical' | 'none'
type buttonSize = 'default' | 'compact'

interface Props {
    text: string,
    subtitile?: string
    icon?: buttonIcon,
    styles: buttonStyles,
    subtitleStyle?: subtitleStyles,
    isDisabled?: boolean,
    fullWidth?: boolean,
    vibration?: vibrationType // Новый проп для типа вибрации
    size?: buttonSize
}

const emit = defineEmits<{
    onSubmit: [event: MouseEvent]
}>()

const props = withDefaults(defineProps<Props>(), {
    vibration: 'button', // По умолчанию используем стандартную вибрацию кнопки
    size: 'default'
})

const { vibratePattern } = useVibration()

// Обработчик клика с вибрацией
const handleClick = async (event: MouseEvent) => {
    if (props.isDisabled) return
    
    // Вызываем вибрацию только если она не отключена
    if (props.vibration !== 'none') {
        await vibratePattern(props.vibration)
    }
    
    emit('onSubmit', event)
}

const iconComponents = {
    Wallet,
    WalletBlack,
    Gear,
    Message,
    Referal,
    User,
    WalletOff,
    Quit,
    ChangePassword
}

</script>

<style lang="scss">
.button-base-block {
    display: flex;
    flex-direction: column;
    align-items: stretch;

    &--full-width {
        width: 100%;
    }
}

.button-base {
    all: unset;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    border-radius: 12px;
    padding: 12px 16px;
    min-height: 48px;
    color: $secondary-300;
    transition: opacity 0.2s ease;
    box-sizing: border-box;
    
    &.full-width {
        width: 100%;
    }

    &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
    }

    &__left-row {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
    }

    &__subtitle {
        flex-shrink: 0;
        padding-left: 8px;
        text-align: right;

        &.regular {
            @include p2-regular;
            color: $primary-100;
        }

        &.medium {
            @include p1-medium;
        }
    }

    &--compact {
        min-height: 32px;
        padding: 8px 12px;
        border-radius: 999px;
        gap: 8px;
    }
}

.button-icon {
    flex-shrink: 0;
}

.primary-gradient {
    @include p2-medium;
    width: auto;
    // height: 48px;
    background: $primary-gradient;
    color: $neutral-200;
    
    &:hover:not(.disabled) {
        background: $primary-gradient-active;
    }
}

.secondary-100 {
    @include p2-medium;
    // height: 48px;
    background: $secondary-100;
    color: $neutral-100;
    
    &:hover:not(.disabled) {
        background: $secondary-100-active;
    }
}

.secondary-200 {
    @include p2-medium;
    // height: 48px;
    background: $secondary-200;
    color: $neutral-100;
    
    &:hover:not(.disabled) {
        background: $secondary-200-active;
    }
}
</style>
